import { KnowledgeArticle, createArticle } from '../types'

export const usersArticles: KnowledgeArticle[] = [
  createArticle({
    id: 'user-roles',
    title: 'User Roles & Permissions',
    section: 'users',
    summary: 'Three roles control access: Admin (full access), Contributor (edit data), and Viewer (read-only). Admins manage users from Settings.',
    content: `
# User Roles & Permissions

The GRC platform has three user roles with different permission levels to ensure appropriate access control.

## Role Overview

### Admin
Full administrative access to the platform.

**Can do:**
- All Contributor permissions
- Manage users (add, edit, deactivate)
- Configure integrations
- Manage organization settings
- View audit logs
- Delete any content

### Contributor
Standard user with edit permissions.

**Can do:**
- View all data
- Create and edit controls
- Upload evidence
- Create and edit documents
- Manage risks
- Create and complete tasks
- Run scans and assessments

**Cannot do:**
- Manage users
- Configure integrations
- Change organization settings

### Viewer
Read-only access to the platform.

**Can do:**
- View all data
- Download reports
- Export data (where allowed)

**Cannot do:**
- Create or edit any data
- Upload evidence
- Make any changes

## Permission Matrix

| Feature | Admin | Contributor | Viewer |
|---------|-------|-------------|--------|
| View dashboard | Yes | Yes | Yes |
| View controls | Yes | Yes | Yes |
| Edit controls | Yes | Yes | No |
| Upload evidence | Yes | Yes | No |
| Manage documents | Yes | Yes | No |
| Create tasks | Yes | Yes | No |
| Run scans | Yes | Yes | No |
| Manage vendors | Yes | Yes | No |
| Manage users | Yes | No | No |
| Configure integrations | Yes | No | No |
| Organization settings | Yes | No | No |

## Choosing Roles

**Assign Admin to:**
- GRC program managers
- IT administrators
- Compliance officers

**Assign Contributor to:**
- Control owners
- Evidence collectors
- Risk managers
- Security team members

**Assign Viewer to:**
- Auditors (external)
- Executive stakeholders
- Team members who need visibility only
    `.trim(),
    tags: ['users', 'roles', 'permissions', 'admin', 'access-control', 'security'],
    relatedPages: ['/users', '/settings'],
    icon: 'Users',
    order: 1,
  }),

  createArticle({
    id: 'managing-users',
    title: 'Managing Users',
    section: 'users',
    summary: 'Add new users with email invitations, edit user details and roles, deactivate users who leave. Passwordless authentication via email verification.',
    content: `
# Managing Users

Administrators can manage user accounts from the Users page.

## Adding Users

1. Go to **Users** (admin only)
2. Click **Add User**
3. Enter user details:
   - Email address (required)
   - Full name
   - Role (Admin, Contributor, Viewer)
4. Click **Create User**

### What Happens Next
- User receives a welcome email
- Email contains login instructions
- User signs in with email verification
- No password needed (passwordless auth)

## Editing Users

1. Find the user in the list
2. Click the edit icon
3. Update details:
   - Name
   - Email
   - Role
4. Click **Save**

### Role Changes
When changing a user's role:
- New permissions take effect immediately
- User may need to refresh their session
- Audit log records the change

## Deactivating Users

When someone leaves the organization:
1. Find the user
2. Click **Deactivate**
3. Confirm the action

### What Happens
- User cannot log in
- Account is preserved for audit trail
- Assigned tasks remain (reassign them)
- User's history is retained

### Reactivating
Deactivated users can be reactivated:
1. Filter to show deactivated users
2. Click **Activate** on the user
3. User can log in again

## Authentication

### Passwordless Login
The platform uses passwordless authentication:
1. User enters their email
2. Verification code is sent to email
3. User enters the 6-digit code
4. Session is established (24-hour expiration)

### Benefits
- No passwords to forget or reset
- No password-based attacks
- Simple user experience
- Email-based account verification

## User Activity

### Audit Trail
User actions are logged:
- Login/logout events
- Data changes
- Evidence uploads
- Configuration changes

### Viewing Activity
1. Open the user's profile
2. View recent activity
3. Filter by action type or date

## Best Practices

1. Use meaningful display names
2. Assign minimum required role
3. Deactivate promptly when users leave
4. Review user list periodically
5. Monitor for inactive accounts
    `.trim(),
    tags: ['users', 'management', 'authentication', 'passwordless', 'deactivation'],
    relatedPages: ['/users'],
    icon: 'UserPlus',
    order: 2,
  }),
]
